/**
 * Iter-253 — Admin Creator Network Application Review Queue.
 *
 * /admin/creator-applications — sortable list with quick approve / reject /
 * request-changes actions. Each row opens a detailed drawer.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Loader2, CheckCircle2, XCircle, MessageSquare, ExternalLink,
  Sparkles, Instagram, Music2, Youtube, Facebook,
} from 'lucide-react';
import { toast } from 'sonner';


const STATUS_FILTERS = [
  { id: 'pending',           label: 'Pending',           color: 'bg-amber-100 text-amber-800' },
  { id: 'changes_requested', label: 'Changes Requested', color: 'bg-orange-100 text-orange-800' },
  { id: 'approved',          label: 'Approved',          color: 'bg-emerald-100 text-emerald-800' },
  { id: 'rejected',          label: 'Rejected',          color: 'bg-rose-100 text-rose-700' },
];


export default function AdminCreatorApplications() {
  const [filter, setFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/creator-applications?status=${filter}&limit=100`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="admin-creator-applications">
      <div className="bg-gradient-to-br from-purple-700 to-pink-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> Creator Network Applications
          </h1>
          <p className="text-white/80 text-sm mt-1">
            Review, approve, reject or request changes on Creator Network applications.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-4 space-y-4">
        {/* Status filter pills */}
        <div className="flex gap-2 overflow-x-auto" data-testid="creator-app-filters">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.id}
              onClick={() => setFilter(s.id)}
              className={`px-3 py-1.5 rounded-full border text-xs font-semibold whitespace-nowrap ${
                filter === s.id ? `${s.color} border-current` : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`creator-app-filter-${s.id}`}
            >
              {s.label}
            </button>
          ))}
          <div className="text-xs text-slate-500 self-center ml-2">
            {loading ? '' : `${total} ${total === 1 ? 'application' : 'applications'}`}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-slate-500 text-sm">No applications in this status.</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <ApplicationRow
                key={it.id}
                app={it}
                onOpen={() => setSelected(it)}
              />
            ))}
          </div>
        )}
      </div>

      <ApplicationReviewDrawer
        app={selected}
        onClose={() => setSelected(null)}
        onAction={() => { setSelected(null); load(); }}
      />
    </div>
  );
}


const ApplicationRow = ({ app, onOpen }) => {
  const socials = app.socials || {};
  return (
    <Card
      onClick={onOpen}
      className="cursor-pointer hover:border-purple-300 transition-all"
      data-testid={`creator-app-row-${app.id}`}
    >
      <CardContent className="p-3 flex items-center gap-3">
        {app.talent_photo ? (
          <img src={app.talent_photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-purple-100 grid place-items-center text-purple-700 font-bold text-sm flex-shrink-0">
            {(app.talent_name || '?').slice(0, 1)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{app.talent_name}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {[app.talent_city, app.talent_state].filter(Boolean).join(', ') || app.talent_email}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {app.creator_type && (
              <Badge className="text-[9px] bg-purple-100 text-purple-700 border-purple-200">
                {app.creator_type === 'both' ? 'Influencer + Creator'
                 : app.creator_type === 'influencer' ? 'Influencer'
                 : 'Content Creator'}
              </Badge>
            )}
            {socials.instagram?.handle && (
              <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                <Instagram className="w-2.5 h-2.5" /> {socials.instagram.followers ? `${Math.round(socials.instagram.followers/1000)}K` : socials.instagram.handle}
              </span>
            )}
            {socials.tiktok?.handle && (
              <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                <Music2 className="w-2.5 h-2.5" /> {socials.tiktok.followers ? `${Math.round(socials.tiktok.followers/1000)}K` : socials.tiktok.handle}
              </span>
            )}
            {socials.youtube?.handle && (
              <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                <Youtube className="w-2.5 h-2.5" /> {socials.youtube.followers ? `${Math.round(socials.youtube.followers/1000)}K` : socials.youtube.handle}
              </span>
            )}
          </div>
        </div>
        <div className="text-[10px] text-slate-400 self-start whitespace-nowrap">
          {new Date(app.submitted_at).toLocaleDateString()}
        </div>
      </CardContent>
    </Card>
  );
};


const ApplicationReviewDrawer = ({ app, onClose, onAction }) => {
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  if (!app) return null;

  const act = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/admin/creator-applications/${app.id}/${path}`, body);
      toast.success(`${path === 'approve' ? 'Approved' : path === 'reject' ? 'Rejected' : 'Changes requested'}.`);
      onAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const socials = app.socials || {};

  return (
    <Dialog open={!!app} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`creator-app-detail-${app.id}`}>
        <DialogHeader>
          <DialogTitle>Review Application</DialogTitle>
        </DialogHeader>

        {/* Talent header */}
        <div className="flex items-center gap-3 border-b pb-3">
          {app.talent_photo ? (
            <img src={app.talent_photo} alt="" className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-purple-100 grid place-items-center text-purple-700 font-bold text-lg">
              {(app.talent_name || '?').slice(0, 1)}
            </div>
          )}
          <div>
            <div className="text-base font-bold">{app.talent_name}</div>
            <div className="text-xs text-slate-500">{app.talent_email}</div>
            {(app.talent_city || app.talent_state) && (
              <div className="text-xs text-slate-500">{[app.talent_city, app.talent_state].filter(Boolean).join(', ')}</div>
            )}
          </div>
          <Badge className="ml-auto text-[10px] bg-purple-100 text-purple-700 border-purple-200">
            {app.creator_type === 'both' ? 'Influencer + Creator'
             : app.creator_type === 'influencer' ? 'Influencer'
             : app.creator_type === 'content_creator' ? 'Content Creator' : 'Creator'}
          </Badge>
        </div>

        {/* Why blurb */}
        {app.why_blurb && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Why iEvntür</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{app.why_blurb}</p>
          </div>
        )}

        {/* Portfolio links */}
        {app.portfolio_links?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Portfolio</div>
            <ul className="space-y-1.5">
              {app.portfolio_links.map((l, i) => (
                <li key={i}>
                  <a href={l.url} target="_blank" rel="noreferrer" className="text-sm text-purple-700 hover:underline inline-flex items-center gap-1">
                    {l.label} <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Socials */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Social handles</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { key: 'instagram', icon: Instagram, base: 'https://instagram.com/' },
              { key: 'tiktok',    icon: Music2,    base: 'https://www.tiktok.com/@' },
              { key: 'youtube',   icon: Youtube,   base: 'https://youtube.com/@' },
              { key: 'facebook',  icon: Facebook,  base: 'https://facebook.com/' },
            ].map((p) => {
              const s = socials[p.key];
              if (!s?.handle) return null;
              const handle = s.handle.replace(/^@/, '');
              return (
                <a
                  key={p.key}
                  href={p.base + handle}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded p-2"
                >
                  <p.icon className="w-4 h-4 text-purple-600" />
                  <span className="font-semibold">@{handle}</span>
                  {s.followers > 0 && <span className="text-xs text-slate-500 ml-auto">{s.followers.toLocaleString()}</span>}
                  <ExternalLink className="w-3 h-3 text-slate-400" />
                </a>
              );
            })}
          </div>
        </div>

        {/* Reviewer action panel */}
        {(app.status === 'pending' || app.status === 'changes_requested') && (
          <div className="border-t pt-3 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Reviewer notes (visible to talent)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — leave context, kudos, or 'what to fix'."
                rows={3}
                data-testid="creator-app-reviewer-notes"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                disabled={busy || !notes}
                onClick={() => act('request-changes', { reviewer_notes: notes })}
                data-testid="creator-app-request-changes"
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1" /> Request changes
              </Button>
              <Button
                variant="outline"
                className="border-rose-300 text-rose-700"
                disabled={busy}
                onClick={() => {
                  if (!reason && !notes) { toast.error('Add a rejection reason'); return; }
                  act('reject', { reviewer_notes: notes, rejection_reason: reason || notes });
                }}
                data-testid="creator-app-reject"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
              </Button>
              <Button
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => act('approve', { reviewer_notes: notes })}
                data-testid="creator-app-approve"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
              </Button>
            </div>
            <div className="text-[10px] text-slate-500">
              Optional rejection reason (used only when rejecting):
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. portfolio links don't match Creator Type"
                className="block w-full mt-1 text-xs border border-slate-200 rounded px-2 py-1"
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
